import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getTargetDetail, updateTarget } from 'config/api';
import { useRouting } from 'hooks/useRouting';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { formatDate } from 'utils/formatter';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import Loading from 'components/commons/loading/Loading';
import {
  Target,
  TargetModalStatus,
  TargetType,
  GlobalTargetProps,
} from '../utils/type';
import { TargetValidateError, validateTarget } from '../utils/validate';
import { targetTypeToTitle } from '../utils/targetInfo';
import { useBirthday } from '../utils/birthday';
import { InputRow } from '../components/commons/InputRow';
import { SetPropertiesBase } from '../components/features/share/SetPropertiesBase';
import { useTargetTypeForm } from '../hooks/useTargetTypeForm';
import { useTargetTitleForm } from '../hooks/useTargetTitleForm';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import { dateToString } from '../utils/utils';

const TargetSetTitleInternal = ({
  id,
  initialTarget,
  birthday,
}: {
  id: string;
  initialTarget: Target;
  birthday: Date;
}) => {
  // Google tag manager
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('chrome') !== -1) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'target_detail',
        targetinfo: `${initialTarget.id}_${
          initialTarget.title
        }_${targetTypeToTitle(initialTarget.refType)}_${formatDate(
          new Date(),
          'yyyyMMddHHmmss'
        )}`,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors_, setErrors] = useState<
    | { tag: 'errorDisabled' }
    | { tag: 'errorEnabled'; errors: TargetValidateError[] }
  >({ tag: 'errorDisabled' });

  const errors = (() => {
    switch (errors_.tag) {
      case 'errorDisabled':
        return null;
      case 'errorEnabled':
        return errors_.errors;
    }
  })();

  const titleErrors = useMemo(
    () =>
      (errors ?? []).flatMap(e => (e.tag === 'TitleError' ? [e.error] : [])),

    [errors]
  );

  const { TargetTypeForm, targetType } = useTargetTypeForm(
    initialTarget.refType
  );

  const { TargetTitleForm, targetTitle } = useTargetTitleForm(
    titleErrors,
    initialTarget.title
  );

  const target = useMemo(
    () =>
      validateTarget({
        targetType,
        targetTitle,
        targetAmount: initialTarget.targetAmount,
        assetAmount: 0,
        targetYear: new Date(initialTarget.endDate).getFullYear(),
        achieved: false,
        thisYear: new Date().getFullYear(),
        birthYear: birthday.getFullYear(),
      }),
    [targetTitle, targetType, initialTarget, birthday]
  );

  useEffect(() => {
    setErrors(() => {
      return {
        tag: 'errorEnabled',
        errors: target.tag === 'Error' ? target.errors : [],
      };
    });
  }, [target]);

  const onFinish = useCallback(async (): Promise<void> => {
    switch (target.tag) {
      case 'Success':
        const accountIDList = initialTarget.accountList.map(
          list => list.account
        );
        const startDate = new Date(initialTarget.startDate);
        const endDate = new Date(initialTarget.endDate);
        const updTargetData = {
          ...initialTarget,
          ...target.value,
          accountIDList,
          startDate,
          endDate,
        };
        await updateTargetAPI(id, updTargetData);

        back();
        break;
      case 'Error':
        setErrors({ tag: 'errorEnabled', errors: target.errors });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTarget, target]);

  const routing = useRouting();
  const back = (): void => {
    routing.backRouting(true);
  };

  return (
    <>
      <SetPropertiesBase
        {...{
          finishButton: {
            message: <>決定</>,
            isDisabled: target.tag === 'Error',
          },
          onCancel: back,
          onFinish,
        }}>
        <InputRow {...{ title: '目標のタイトル' }} className="mt-6">
          {TargetTitleForm}
        </InputRow>
        <InputRow {...{ title: '目標カテゴリ' }} className="mt-4">
          {TargetTypeForm}
        </InputRow>
      </SetPropertiesBase>
    </>
  );
};

const TargetSetTitle = (): React.ReactElement => {
  useCustomHeaderTitle('目標タイトルの編集');
  const { id } = useParams<{ id: string }>();

  const { setIsShowMenu } = useGlobalMenuContext();
  useEffect(() => {
    return setIsShowMenu(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { setWithBackIconWord } = useCustomizeHeaderContext();
  useEffect(() => {
    setWithBackIconWord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching } = useQuery(
    'targetSetTitleTargetDetail',
    () => getTargetDetailAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );
  const { birthday } = useBirthday();

  if (data === null || data === undefined || isFetching)
    return (
      <>
        <Loading loading={true} style={{ marginTop: '3rem' }} />
      </>
    );

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetSetTitleInternal {...{ id, initialTarget: data, birthday }} />
    </PremiumCheck>
  );
};

const getTargetDetailAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

const updateTargetAPI = async (
  id: string,
  target: GlobalTargetProps
): Promise<void> => {
  await updateTarget({
    ID: id,
    TargetAmount: target.targetAmount,
    EndDate: dateToString(target.endDate),
    StartDate: dateToString(target.startDate),
    Title: target.title,
    RefType: target.refType,
    ...(target.excludeAmount !== null &&
    target.reserveAmount !== null &&
    target.accountIDList !== null
      ? {
          AccountIDList: target.accountIDList,
          ExcludeAmount: target.excludeAmount,
          ReserveAmount: target.reserveAmount,
        }
      : {}),
    ...(target.managementType
      ? { ManagementType: target.managementType }
      : null),
  });
};

export default TargetSetTitle;
